// La géométrie d'un anneau de répartition, et le découpage des parts.
//
// Séparé de l'écran à dessein : ce sont deux fonctions de calcul, sans DOM ni réseau, et
// ce sont précisément celles dont une erreur ne se voit pas. Un arc tracé un quart de tour
// trop loin, une part oubliée dans le regroupement de la queue — l'anneau reste joli et
// ment. Ici, elles s'éprouvent.

/**
 * Le tracé d'une part d'anneau.
 *
 * Les angles partent de midi et tournent dans le sens des aiguilles, parce que c'est le
 * sens dans lequel on lit un camembert — et non celui du repère trigonométrique, qui
 * ferait démarrer la plus grosse part à trois heures sans raison lisible.
 */
export const part = (cx: number, cy: number, R: number, r: number, a0: number, a1: number): string => {
  const d2 = (n: number) => Math.round(n * 100) / 100;
  const point = (rayon: number, a: number) => `${d2(cx + rayon * Math.sin(a))} ${d2(cy - rayon * Math.cos(a))}`;
  const grand = a1 - a0 > Math.PI ? 1 : 0;
  return `M${point(R, a0)} A${d2(R)} ${d2(R)} 0 ${grand} 1 ${point(R, a1)}`
       + ` L${point(r, a1)} A${d2(r)} ${d2(r)} 0 ${grand} 0 ${point(r, a0)} Z`;
};

export interface PartAnneau {
  /** La case d'origine, pour que la part reste cliquable. Vide sur une queue regroupée. */
  cle: string;
  libelle: string;
  n: number;
  /** Ce que la part regroupe, quand elle en regroupe — vide sinon. */
  detail: string;
  a0: number;
  a1: number;
}

/**
 * Les parts d'un axe, de la plus grosse à la plus petite, angles compris.
 *
 * Au-delà de `maxParts`, la queue se regroupe : un camembert à vingt segments ne se lit
 * pas, et les quinze derniers pèsent un profil chacun. Le regroupement garde le détail
 * dans la part plutôt que de le faire disparaître — c'est la différence entre simplifier
 * et tronquer.
 */
export function decouper(
  cases: { cle?: string; libelle: string; n: number }[], maxParts: number, ordonne = false,
): { parts: PartAnneau[]; total: number } {
  // Un axe ordonné garde l'ordre de sa liste : la chaîne de valeur est une séquence —
  // cadrage, preuve de concept, industrialisation — et les crans de profondeur une
  // échelle. Les trier par effectif mettait « Gouvernance » avant « Exploitation » et
  // faisait lire un enchaînement qui n'existe pas. La teinte, alors, code la position
  // dans la séquence plutôt que la grandeur, ce qui est la bonne lecture des deux fois.
  const pleines = cases.filter((x) => x.n > 0);
  if (!ordonne) pleines.sort((a, b) => b.n - a.n || a.libelle.localeCompare(b.libelle));
  if (!pleines.length) return { parts: [], total: 0 };

  // Rien ne se regroupe sur un axe ordonné : amputer la fin d'une séquence la rendrait
  // fausse, là où regrouper la queue d'un classement reste vrai.
  const gardees = !ordonne && pleines.length > maxParts ? pleines.slice(0, maxParts - 1) : pleines;
  const queue = pleines.slice(gardees.length);
  const brutes = [
    ...gardees.map((x) => ({ cle: x.cle ?? '', libelle: x.libelle, n: x.n, detail: '' })),
    ...(queue.length ? [{
      // La queue regroupée n'a pas de case unique à ouvrir : sa clé reste vide, et
      // l'appelant sait alors qu'il n'y a nulle part où mener.
      cle: '',
      libelle: `Autres · ${queue.length} case${queue.length > 1 ? 's' : ''}`,
      n: queue.reduce((s, x) => s + x.n, 0),
      detail: queue.map((x) => `${x.libelle} (${x.n})`).join(', '),
    }] : []),
  ];
  const total = brutes.reduce((s, x) => s + x.n, 0);

  // Les angles se cumulent, et le dernier se ferme exactement sur le tour complet : une
  // somme de fractions arrondies laisserait sinon un cheveu de fond entre la dernière
  // part et la première.
  let angle = 0;
  const parts = brutes.map((x, i) => {
    const a0 = angle;
    angle = i === brutes.length - 1 ? 2 * Math.PI : angle + (x.n / total) * 2 * Math.PI;
    return { ...x, a0, a1: angle };
  });
  return { parts, total };
}
